import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Bot, FolderPlus, Send, Cpu, ShieldCheck, Plus, ListChecks, Play, XCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { luxe, PILLARS, gradientCss } from '@/lib/luxe';
import { GlassCard, LuxeButton, SectionTitle, Input, Textarea, Field, Spinner, Empty } from '@/components/bits';
import { MyraBuildChat } from '@/components/MyraBuildChat';
import { CodingConsole } from '@/components/CodingConsole';

const P = PILLARS.coding;
const RUNNING = ['awaiting_approval', 'running'];

const TEMPLATES = [
  { label: '📝 Todo REST API', prompt: 'Build a REST API with GET/POST/DELETE /todos endpoints backed by an in-memory store, plus a short README explaining how to run it.' },
  { label: '🌐 Landing Page', prompt: 'Create a modern responsive landing page (index.html + style.css) for a startup: hero with headline + CTA, a 3-feature section, and a footer. Use clean modern styling.' },
  { label: '🐍 Python CLI', prompt: 'Build a Python CLI tool that takes a folder path and prints a summary of file counts grouped by extension, with a --json output option.' },
  { label: '⚡ Express Server', prompt: 'Create a minimal Express.js server (server.js + package.json) with a GET /health route and a POST /api/echo route that returns the JSON body.' },
  { label: '🎨 Portfolio Site', prompt: 'Build a single-page personal portfolio website (index.html + style.css) with sections for About, Projects (3 cards), and Contact. Dark theme, smooth and elegant.' },
];

function AgentChat({ agent }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    const q = text.trim(); setText('');
    setMsgs((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const r = await api('/admin/agents/chat', { method: 'POST', body: { agent_id: agent.id, message: q } });
      setMsgs((m) => [...m, { role: 'assistant', content: r.message, benefits: r.benefits, risks: r.risks }]);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="space-y-3 max-h-[360px] overflow-y-auto mb-4 pr-1">
        {msgs.length === 0 && <Empty icon={Bot} title="Ask this agent anything" desc={`Model: ${agent.model}`} />}
        {msgs.map((m, i) => (
          <div key={i} className="p-3 rounded-xl" style={{ background: m.role === 'user' ? 'rgba(37,99,235,0.08)' : luxe.surface2, border: `1px solid ${luxe.border}` }}>
            <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: m.role === 'user' ? luxe.blue : P.primary }}>{m.role === 'user' ? 'You' : agent.name}</div>
            <div className="text-sm whitespace-pre-wrap" style={{ color: luxe.text }}>{m.content}</div>
            {m.benefits?.length > 0 && <ul className="mt-2 text-xs list-disc pl-4" style={{ color: luxe.success }}>{m.benefits.map((b, j) => <li key={j}>{b}</li>)}</ul>}
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-sm" style={{ color: luxe.muted }}><Spinner /> Thinking…</div>}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Type your message…" data-testid="agent-chat-input" />
        <LuxeButton variant="pillar" color={P} onClick={send} disabled={busy}><Send size={16} /></LuxeButton>
      </div>
    </div>
  );
}

function TaskRunner({ agent, projects, onCreateProject }) {
  const buildProjects = projects.filter((p) => p.id !== 'cambri');
  const [projectId, setProjectId] = useState(buildProjects[0]?.id || '');
  const [prompt, setPrompt] = useState('');
  const [budget, setBudget] = useState('medium');
  const [task, setTask] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => { if (!projectId && buildProjects[0]) setProjectId(buildProjects[0].id); }, [projects]); // eslint-disable-line

  const poll = async (id) => {
    try {
      const t = await api(`/admin/agents/task/${id}`);
      setTask(t);
      if (!RUNNING.includes(t.status)) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch { /* keep polling */ }
  };
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const generatePlan = async () => {
    if (!projectId) return toast.error('Create/select a build project first');
    if (prompt.trim().length < 5) return toast.error('Describe the task in more detail');
    setBusy(true); setTask(null);
    try {
      const t = await api('/admin/agents/task/start', { method: 'POST', body: { agent_id: agent.id, prompt, budget, project: projectId } });
      setTask(t);
      toast.success('Plan ready — review & approve to build');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const approve = async () => {
    try {
      await api(`/admin/agents/task/${task.id}/approve`, { method: 'POST' });
      toast.success('Build started');
      setTask((t) => ({ ...t, status: 'running' }));
      pollRef.current = setInterval(() => poll(task.id), 3000);
      poll(task.id);
    } catch (e) { toast.error(e.message); }
  };

  const cancel = async () => {
    try { await api(`/admin/agents/task/${task.id}/cancel`, { method: 'POST' }); poll(task.id); } catch (e) { toast.error(e.message); }
  };

  const plan = task?.plan || {};
  const steps = task?.steps || [];
  const final = task?.final;

  return (
    <div>
      {!task && (
        <>
          {buildProjects.length === 0 ? (
            <GlassCard className="p-5 mb-4">
              <div className="text-sm mb-3" style={{ color: luxe.textMid }}>Agents build inside an isolated project. Create one to begin:</div>
              <LuxeButton variant="pillar" color={P} onClick={onCreateProject}><Plus size={16} className="inline mr-1" />Create a Project</LuxeButton>
            </GlassCard>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Build Project">
                  <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: '#0B0D1E', color: luxe.text }} data-testid="task-project">
                    {buildProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Budget (LLM calls)">
                  <select value={budget} onChange={(e) => setBudget(e.target.value)} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: '#0B0D1E', color: luxe.text }}>
                    <option value="small">Small (20)</option><option value="medium">Medium (50)</option><option value="unlimited">Large (200)</option>
                  </select>
                </Field>
              </div>
              <div className="mb-1 text-[11px] font-extrabold uppercase tracking-widest" style={{ color: luxe.muted }}>Quick-start templates</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {TEMPLATES.map((t) => (
                  <button key={t.label} onClick={() => setPrompt(t.prompt)} data-testid={`template-${t.label}`} className="px-3 py-2 rounded-full text-sm font-semibold luxe-btn"
                    style={{ background: prompt === t.prompt ? gradientCss(P.grad) : luxe.surface2, color: prompt === t.prompt ? '#fff' : luxe.textMid, border: `1px solid ${luxe.border}` }}>
                    {t.label}
                  </button>
                ))}
              </div>
              <Field label="Task for the agent"><Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Build a REST API with a /todos endpoint and in-memory store" data-testid="task-prompt" /></Field>
              <LuxeButton variant="pillar" color={P} onClick={generatePlan} disabled={busy} data-testid="task-generate-plan">
                {busy ? <span className="flex gap-2 items-center"><Spinner color="#fff" /> Planning…</span> : <><Sparkles size={16} className="inline mr-1" />Generate Build Plan</>}
              </LuxeButton>
            </>
          )}
        </>
      )}

      {task && (
        <div className="float-in">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: task.status === 'completed' ? 'rgba(14,169,114,0.12)' : task.status === 'running' ? 'rgba(37,99,235,0.1)' : luxe.surface2, color: task.status === 'completed' ? luxe.success : task.status === 'running' ? luxe.blue : luxe.textMid, border: `1px solid ${luxe.border}` }}>
                {task.status === 'running' && <Loader2 size={12} className="inline animate-spin mr-1" />}{task.status}
              </span>
              <span className="text-xs" style={{ color: luxe.muted }}>{task.calls_used || 0} calls used</span>
            </div>
            <LuxeButton variant="ghost" onClick={() => setTask(null)}>New Task</LuxeButton>
          </div>

          <GlassCard className="p-4 mb-3">
            <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: P.primary }}>Plan</div>
            <div className="text-sm font-semibold" style={{ color: luxe.text }}>{plan.summary}</div>
            {(plan.steps || []).length > 0 && (
              <ol className="mt-2 space-y-1 text-xs" style={{ color: luxe.textMid }}>
                {plan.steps.map((s, i) => <li key={i}>{s.n || i + 1}. {s.action} {s.risk && <span style={{ color: luxe.muted }}>({s.risk})</span>}</li>)}
              </ol>
            )}
            {(plan.benefits || []).length > 0 && <ul className="mt-2 text-xs list-disc pl-4" style={{ color: luxe.success }}>{plan.benefits.map((b, i) => <li key={i}>{b}</li>)}</ul>}
            {(plan.risks || []).length > 0 && <ul className="mt-1 text-xs list-disc pl-4" style={{ color: luxe.warning }}>{plan.risks.map((b, i) => <li key={i}>{b}</li>)}</ul>}
          </GlassCard>

          {task.status === 'awaiting_approval' && (
            <div className="flex gap-2 mb-4">
              <LuxeButton variant="pillar" color={P} onClick={approve} data-testid="task-approve"><Play size={15} className="inline mr-1" />Approve & Build</LuxeButton>
              <LuxeButton variant="danger" onClick={() => setTask(null)}><XCircle size={15} className="inline mr-1" />Discard</LuxeButton>
            </div>
          )}
          {task.status === 'running' && <div className="mb-4"><LuxeButton variant="danger" onClick={cancel}>Cancel Build</LuxeButton></div>}

          {steps.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-bold" style={{ color: luxe.text }}>Execution</div>
              {steps.map((s, i) => (
                <GlassCard key={i} className="p-3">
                  <div className="flex items-center gap-2">
                    {s.ok ? <CheckCircle2 size={14} style={{ color: luxe.success }} /> : <XCircle size={14} style={{ color: luxe.danger }} />}
                    <span className="text-xs font-bold" style={{ color: luxe.text }}>#{s.n} · {s.tool}</span>
                  </div>
                  {s.thought && <div className="text-xs mt-1" style={{ color: luxe.textMid }}>{s.thought}</div>}
                  {s.result_preview && <div className="text-[11px] mt-1 font-mono p-2 rounded-lg" style={{ background: luxe.surface2, color: luxe.muted, maxHeight: 120, overflow: 'auto' }}>{s.result_preview}</div>}
                  {s.error && <div className="text-[11px] mt-1" style={{ color: luxe.danger }}>{s.error}</div>}
                </GlassCard>
              ))}
            </div>
          )}

          {final && (
            <GlassCard className="p-4 mt-3" style={{ border: `1.5px solid ${luxe.success}` }}>
              <div className="text-[10px] font-extrabold uppercase tracking-widest mb-1" style={{ color: luxe.success }}>Build Complete</div>
              <div className="text-sm" style={{ color: luxe.text }}>{final.summary}</div>
            </GlassCard>
          )}
        </div>
      )}
    </div>
  );
}

function AgentPanel({ agent, projects, onBack, onCreateProject }) {
  const [mode, setMode] = useState('task');
  return (
    <GlassCard className="p-5 float-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-2xl">{agent.emoji}</div>
          <div>
            <div className="font-bold" style={{ color: luxe.text }}>{agent.name}</div>
            <div className="text-xs" style={{ color: luxe.muted }}>{agent.tagline}</div>
          </div>
        </div>
        <LuxeButton variant="ghost" onClick={onBack}>Back</LuxeButton>
      </div>
      <div className="flex gap-2 mb-4">
        {[['task', 'Assign Task', ListChecks], ['chat', 'Quick Chat', Bot]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setMode(id)} className="px-3 py-2 rounded-full text-sm font-bold luxe-btn"
            style={mode === id ? { background: gradientCss(P.grad), color: '#fff' } : { background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
            <Icon size={14} className="inline mr-1" />{label}
          </button>
        ))}
      </div>
      {mode === 'task'
        ? (agent.can_write ? <TaskRunner agent={agent} projects={projects} onCreateProject={onCreateProject} /> : <Empty icon={ShieldCheck} title="Read-only agent" desc="This agent analyses/tests — use Quick Chat, or pick a builder agent to assign a task." />)
        : <AgentChat agent={agent} />}
    </GlassCard>
  );
}

export default function Coding() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'studio_admin';
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [newProj, setNewProj] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const pr = await api('/admin/agents/projects').catch(() => []);
      setProjects(Array.isArray(pr) ? pr : pr.items || []);
      setAgents((await api('/admin/agents').catch(() => [])) || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const createProject = async (name) => {
    const n = (name || newProj).trim();
    if (!n) return toast.error('Enter a project name');
    try {
      await api('/admin/agents/projects/create', { method: 'POST', body: { name: n } });
      toast.success('Project created'); setNewProj(''); await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Pillar 1 · Software Coding" title="AI Coding Agents" color={P.primary}
        desc="Assign a task to a specialist agent inside an isolated project — it plans, you approve, then it builds and tests autonomously." />

      {loading ? <div className="flex justify-center py-16"><Spinner size={28} /></div> : active ? (
        <AgentPanel agent={active} projects={projects} onBack={() => setActive(null)} onCreateProject={() => createProject(`Project ${projects.length + 1}`)} />
      ) : (
        <>
          <MyraBuildChat projects={projects} onCreateProject={() => createProject(`Project ${projects.length + 1}`)} />
          {(
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold" style={{ color: luxe.text }}>AI Agent Suite <span style={{ color: luxe.muted }}>({agents.length} agents · pick one to build)</span></div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {agents.map((a) => (
                  <GlassCard key={a.id} data-testid={`agent-${a.id}`} className="p-5 cursor-pointer hover-lift" onClick={() => setActive(a)}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="text-2xl">{a.emoji}</div>
                      <div className="font-bold" style={{ color: luxe.text }}>{a.name}</div>
                    </div>
                    <p className="text-sm" style={{ color: luxe.textMid }}>{a.tagline}</p>
                    <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: luxe.muted }}>
                      <Cpu size={13} /> {a.model} {a.can_write ? <span style={{ color: luxe.success }}>· builds</span> : <span>· read-only</span>}
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          )}

          <CodingConsole projects={projects} />

          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold" style={{ color: luxe.text }}>Projects</div>
          </div>
          <GlassCard className="p-4 mb-4 flex gap-2">
            <Input value={newProj} onChange={(e) => setNewProj(e.target.value)} placeholder="New project name…" data-testid="new-project-input" />
            <LuxeButton variant="pillar" color={P} onClick={() => createProject()}><Plus size={16} className="inline mr-1" />Create</LuxeButton>
          </GlassCard>
          {projects.filter((p) => p.id !== 'cambri').length === 0 ? <Empty icon={FolderPlus} title="No projects yet" desc="Create your first project to start building." /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.filter((p) => p.id !== 'cambri').map((pr) => (
                <GlassCard key={pr.id} className="p-5 hover-lift">
                  <div className="font-bold" style={{ color: luxe.text }}>{pr.name}</div>
                  <div className="text-xs mt-1" style={{ color: luxe.muted }}>{pr.target || 'app'} · {pr.id}</div>
                </GlassCard>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
